/**
 * SHER-208 / SHER-1130 — shared formatter for the common smartfield record
 * shape: an array of objects with a name-like key and a "<prefix>-code" key
 * (ICD/ICT/ICD-11 diagnoses, background, allergy, treatment, file). Each item
 * formats as "Name (CODE)" so list / table / detail surfaces render the real
 * clinical data instead of "—" or a React #31 crash.
 *
 * Extracted verbatim out of show_records_section.jsx's single-submission detail
 * view so the hospital submissions table (SurveyRecordsTable.renderValue) can
 * produce the exact same text. The two had diverged (SHER-1130): the table had
 * no case for diagnosis/file, so its default branch collapsed those object
 * arrays to the "—" placeholder even though the data was present. Sharing one
 * formatter is what stops the pair from drifting apart again (cf. SHER-172).
 */
export function formatSmartfieldItem(item: any): string {
    if (item == null) return '';
    if (typeof item !== 'object') return String(item);
    const entries = Object.entries(item).filter(([k]) => k !== 'postId' && k !== 'treatmentPostId');
    const codeEntry = entries.find(([k, v]) => k.endsWith('-code') && v != null && v !== '');
    const nameEntry = entries.find(([k, v]) =>
        !k.endsWith('-code') && typeof v === 'string' && v.trim() !== ''
    );
    const name = nameEntry ? String(nameEntry[1]).trim() : '';
    const code = codeEntry ? String(codeEntry[1]).trim() : '';
    if (name && code) return `${name} (${code})`;
    if (name) return name;
    if (code) return code;
    try { return JSON.stringify(item); } catch (_) { return String(item); }
}

/**
 * Apply formatSmartfieldItem across the scalar | array | object shapes a
 * smartfield record value can take. Scalars pass through unchanged (so a plain
 * string/number keeps rendering as before); arrays join their formatted items
 * with ", "; a lone object formats directly. Never returns a raw object/array,
 * so the result is always safe as a React child (SHER-210 contract).
 */
export function formatSmartfieldValue(value: any): string | number | boolean {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
    if (Array.isArray(value)) {
        return value.map(formatSmartfieldItem).filter((s) => s).join(', ');
    }
    if (typeof value === 'object') return formatSmartfieldItem(value);
    try { return JSON.stringify(value); } catch (_) { return String(value); }
}
